import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { signInWithEmailAndPassword, onAuthStateChanged, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { translations } from '../../utils/translations';

function LoginPage() {
  const navigate = useNavigate();
  const { settings } = usePlatformSettings();
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('adminLanguage') || 'en';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const t = translations[language];

  // Check if already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is logged in, check if they have a profile
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Check if password change is required
            if (userData.requiresPasswordChange) {
              setPendingUser(user);
              setShowPasswordChange(true);
              setCheckingAuth(false);
              return;
            }
            navigate('/admin/leads');
          }
        } catch (err) {
          console.error('Error checking user role:', err);
        }
      }
      setCheckingAuth(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get user role from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        setError('User account not found. Please contact an administrator.');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      
      // Check if password change is required
      if (userData.requiresPasswordChange) {
        setPendingUser(user);
        setCurrentPassword(password); // Store for re-authentication
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // All users go to leads page (admins can access dashboard via link)
      navigate('/admin/leads');
    } catch (err) {
      console.error('Login error:', err);
      switch (err.code) {
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password.');
          break;
        case 'auth/invalid-credential':
          setError('Invalid email or password.');
          break;
        default:
          setError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);
    
    // Validate passwords
    if (newPassword.length < 6) {
      setPasswordError(t.admin.passwordTooShort);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError(t.admin.passwordMismatch);
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      // Re-authenticate user first (required before password change)
      const credential = EmailAuthProvider.credential(pendingUser.email, currentPassword);
      await reauthenticateWithCredential(pendingUser, credential);
      
      // Update password
      await updatePassword(pendingUser, newPassword);
      
      // Update Firestore to remove the requiresPasswordChange flag
      await updateDoc(doc(db, 'users', pendingUser.uid), {
        requiresPasswordChange: false,
        passwordChangedAt: new Date(),
      });
      
      // Close dialog and navigate
      setShowPasswordChange(false);
      navigate('/admin/leads');
      
    } catch (err) {
      console.error('Password change error:', err);
      switch (err.code) {
        case 'auth/wrong-password':
          setPasswordError('Current password is incorrect.');
          break;
        case 'auth/weak-password':
          setPasswordError(t.admin.passwordTooShort);
          break;
        default:
          setPasswordError(`Failed to change password: ${err.message}`);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: '#FDF0D5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress sx={{ color: '#EB5E55' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5', py: 8 }}>
      <Container maxWidth="xs">
        {/* Logo / Brand */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: '#3A3335',
              mb: 1,
            }}
          >
            {settings.platformName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#5C5658' }}>
            Admin Portal
          </Typography>
        </Box>

        {/* Login Form */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '16px',
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, color: '#3A3335', fontWeight: 600 }}>
            Sign In
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              variant="outlined"
              autoComplete="email"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              variant="outlined"
              autoComplete="current-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1, borderRadius: '10px' }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !email || !password}
              sx={{
                py: 1.5,
                mt: 1,
                fontWeight: 600,
                borderRadius: '10px',
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#7A7577',
            textAlign: 'center',
            mt: 4,
          }}
        >
          © {new Date().getFullYear()} {settings.companyName}
        </Typography>
      </Container>

      {/* Password Change Dialog */}
      <Dialog
        open={showPasswordChange}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '16px',
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335', fontWeight: 600 }}>
          {t.admin.passwordChangeRequired}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#5C5658', mb: 3 }}>
            {t.admin.passwordChangeMessage}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label={t.admin.currentPassword}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              variant="outlined"
              autoComplete="current-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            
            <TextField
              fullWidth
              label={t.admin.newPassword}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              variant="outlined"
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            
            <TextField
              fullWidth
              label={t.admin.confirmPassword}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              variant="outlined"
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            
            {passwordError && (
              <Alert severity="error" sx={{ borderRadius: '10px' }}>
                {passwordError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="contained"
            onClick={handlePasswordChange}
            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
            sx={{
              py: 1.5,
              px: 4,
              fontWeight: 600,
              borderRadius: '10px',
            }}
          >
            {passwordLoading ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : t.admin.changePassword}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoginPage;
